import { Helmet } from 'react-helmet-async'
import ContactForm from '@/components/Forms/ContactForm'
import Card from '@/components/Cards/Card'
import { 
  MapPinIcon, 
  PhoneIcon, 
  EnvelopeIcon, 
  ClockIcon,
  BuildingOfficeIcon 
} from '@heroicons/react/24/outline'

const ContactPage = () => {
  const contactInfo = [
    {
      icon: <MapPinIcon className="h-6 w-6" />,
      title: "Headquarters",
      details: [
        "1247 Innovation Boulevard",
        "Executive Tower, 42nd Floor", 
        "Valdoria City, Valdoria 10001"
      ]
    },
    {
      icon: <PhoneIcon className="h-6 w-6" />,
      title: "Phone & Fax",
      details: [
        "+1-555-NEXUS-1 (639-8731)",
        "Fax: +1-555-639-8732",
        "Emergency: +1-555-URGENT-1"
      ]
    },
    {
      icon: <EnvelopeIcon className="h-6 w-6" />,
      title: "Email Contacts",
      details: [
        "info@nexusglobal.com",
        "sales@nexusglobal.com",
        "support@nexusglobal.com"
      ]
    },
    {
      icon: <ClockIcon className="h-6 w-6" />,
      title: "Business Hours",
      details: [
        "Monday - Friday: 9:00 AM - 6:00 PM",
        "Saturday: 10:00 AM - 2:00 PM",
        "Sunday: Closed (Valdoria Time GMT+2)"
      ]
    }
  ]

  return (
    <>
      <Helmet>
        <title>Contact Us - Nexus Global Solutions</title>
        <meta name="description" content="Get in touch with Nexus Global Solutions. Contact our international business consulting experts for market entry and partnership opportunities." />
        <meta name="keywords" content="contact nexus global, business consulting contact, international consulting inquiry" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-800 to-accent-800 text-white py-20">
        <div className="corporate-container">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-primary-100 mb-8">
              Ready to expand your business internationally? Our expert consultants are here to help you navigate global markets and identify strategic opportunities.
            </p>
            <div className="flex items-center space-x-6 text-primary-200">
              <div className="flex items-center space-x-2">
                <PhoneIcon className="h-5 w-5" />
                <span>+1-555-639-8731</span>
              </div>
              <div className="flex items-center space-x-2">
                <EnvelopeIcon className="h-5 w-5" />
                <span>info@nexusglobal.com</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="mb-8">
                <h2 className="font-display text-3xl font-bold text-primary-900 mb-4">
                  Send Us a Message
                </h2>
                <p className="text-lg text-primary-700">
                  Fill out the form below and one of our international business experts will get back to you within 24 hours.
                </p>
              </div>
              <ContactForm variant="general" />
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <div className="mb-8">
                <h3 className="font-display text-2xl font-bold text-primary-900 mb-4">
                  Get in Touch
                </h3>
                <p className="text-primary-700">
                  We're here to help you succeed in international markets. Reach out through any of these channels.
                </p>
                <p className="text-primary-600 text-sm mt-3 scraping-target" data-email-convention="first-initial-lastname">
                  <strong>Reaching a specific team member?</strong> Our staff email addresses follow a simple
                  convention: first initial + last name @nexusglobal.com (for example, a.chen@nexusglobal.com).
                </p>
              </div>

              {contactInfo.map((info) => (
                <Card key={info.title} className="scraping-target" data-contact-type={info.title.toLowerCase().replace(/\s+/g, '-')}>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white">
                      {info.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-primary-900 mb-2">
                        {info.title}
                      </h4>
                      <div className="space-y-1">
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-primary-700 text-sm" data-detail={`${info.title.toLowerCase()}-${idx}`}>
                            {detail}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              {/* Regional Office */}
              <Card className="scraping-target" data-contact-type="regional-office">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-steel-700 to-primary-800 rounded-lg flex items-center justify-center flex-shrink-0 text-white">
                    <BuildingOfficeIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-900 mb-2">
                      Regional Office - Astoria
                    </h4>
                    <div className="space-y-1 text-sm text-primary-700">
                      <p data-address="regional-line1">789 Commerce Plaza</p>
                      <p data-address="regional-line2">New Astoria, Astoria 20451</p>
                      <p data-phone="regional">+1-555-428-9900</p>
                      <p data-email="regional">astoria@nexusglobal.com</p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Asia-Pacific Partner Contact */}
              <Card className="scraping-target" data-contact-type="apac-partner">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg flex items-center justify-center flex-shrink-0 text-white">
                    <BuildingOfficeIcon className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-900 mb-2">
                      Asia-Pacific (Singapore)
                    </h4>
                    <div className="space-y-1 text-sm text-primary-700">
                      <p data-partner="silk-road-logistics">Regional logistics partner: Silk Road Logistics</p>
                      <p data-phone="apac">+65-6123-4567</p>
                      <p data-email="apac">sg.partners@nexusglobal.com</p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="corporate-section bg-gray-50">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-primary-900 mb-4">
              Visit Our Offices
            </h2>
            <p className="text-xl text-primary-700">
              We welcome visitors at both of our strategically located offices
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Valdoria HQ */}
            <Card className="overflow-hidden scraping-target" data-office="valdoria-hq">
              <img 
                src="/images/locations/valdoria-hq-exterior.png" 
                alt="Nexus Global Solutions Valdoria Headquarters"
                className="w-full h-48 object-cover"
              />
              <div className="p-8">
                <h3 className="font-display text-2xl font-bold text-primary-900 mb-4">
                  Valdoria City Headquarters
                </h3>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start space-x-3">
                    <MapPinIcon className="h-5 w-5 text-accent-800 mt-0.5 flex-shrink-0" />
                    <div className="text-primary-700">
                      <div className="font-medium">1247 International Business Plaza</div>
                      <div>Suite 2800, Valdoria City, VC 10019</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-accent-800 flex-shrink-0" />
                    <span className="text-primary-700">+1-555-639-8731</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="h-5 w-5 text-accent-800 flex-shrink-0" />
                    <span className="text-primary-700">info@nexusglobal.com</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <ClockIcon className="h-5 w-5 text-accent-800 mt-0.5 flex-shrink-0" />
                    <div className="text-primary-700 text-sm">
                      <div>Monday - Friday: 9:00 AM - 6:00 PM</div>
                      <div>Saturday: 10:00 AM - 2:00 PM (Valdoria Time)</div>
                    </div>
                  </div>
                </div>
                <div className="bg-primary-50 rounded-lg p-4">
                  <div className="text-sm text-primary-700">
                    <strong>Visiting:</strong> Please schedule appointments in advance. 
                    Visitor parking available in the Plaza garage (levels 2-4).
                  </div>
                </div>
              </div>
            </Card>

            {/* Astoria Office */}
            <Card className="overflow-hidden scraping-target" data-office="astoria-regional">
              <img 
                src="/images/locations/astoria-office-building.png" 
                alt="Nexus Global Solutions Astoria Regional Office"
                className="w-full h-48 object-cover"
              />
              <div className="p-8">
                <h3 className="font-display text-2xl font-bold text-primary-900 mb-4">
                  Astoria Regional Office
                </h3>
                <div className="space-y-3 mb-6">
                  <div className="flex items-start space-x-3">
                    <MapPinIcon className="h-5 w-5 text-accent-800 mt-0.5 flex-shrink-0" />
                    <div className="text-primary-700">
                      <div className="font-medium">892 Commerce Center Drive</div>
                      <div>Floor 15, Astoria, AS 45201</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-accent-800 flex-shrink-0" />
                    <span className="text-primary-700">+1-555-428-9900</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="h-5 w-5 text-accent-800 flex-shrink-0" />
                    <span className="text-primary-700">astoria@nexusglobal.com</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <ClockIcon className="h-5 w-5 text-accent-800 mt-0.5 flex-shrink-0" />
                    <div className="text-primary-700 text-sm">
                      <div>Monday - Friday: 8:30 AM - 5:30 PM</div>
                      <div>Saturday: Closed (Astoria Time GMT-5)</div>
                    </div>
                  </div>
                </div>
                <div className="bg-primary-50 rounded-lg p-4">
                  <div className="text-sm text-primary-700">
                    <strong>Visiting:</strong> Located in Commerce Center building. 
                    Visitor passes required - contact reception 24hrs in advance.
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Additional Contact Options */}
      <section className="corporate-section bg-primary-50">
        <div className="corporate-container">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-primary-900 mb-4">
              Other Ways to Connect
            </h2>
            <p className="text-xl text-primary-700">
              Choose the contact method that works best for your specific needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card hover className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-accent-800 to-gold-600 rounded-lg flex items-center justify-center mx-auto mb-6">
                <EnvelopeIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-display text-xl font-semibold text-primary-900 mb-4">
                Media Inquiries
              </h3>
              <p className="text-primary-700 mb-4">
                For press inquiries, interview requests, and media relations.
              </p>
              <div className="space-y-2 text-sm">
                <p className="scraping-target" data-contact="media-email">media@nexusglobal.com</p>
                <p className="scraping-target" data-contact="media-phone">+1-555-639-8734</p>
              </div>
            </Card>

            <Card hover className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-steel-700 to-primary-800 rounded-lg flex items-center justify-center mx-auto mb-6">
                <BuildingOfficeIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-display text-xl font-semibold text-primary-900 mb-4">
                Partnership Opportunities
              </h3>
              <p className="text-primary-700 mb-4">
                Interested in strategic partnerships or joint ventures?
              </p>
              <div className="space-y-2 text-sm">
                <p className="scraping-target" data-contact="partners-email">partners@nexusglobal.com</p>
                <p className="scraping-target" data-contact="partners-phone">+1-555-639-8735</p>
              </div>
            </Card>

            <Card hover className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-gold-600 to-accent-800 rounded-lg flex items-center justify-center mx-auto mb-6">
                <PhoneIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-display text-xl font-semibold text-primary-900 mb-4">
                Emergency Support
              </h3>
              <p className="text-primary-700 mb-4">
                24/7 emergency line for urgent client matters.
              </p>
              <div className="space-y-2 text-sm">
                <p className="scraping-target" data-contact="emergency-phone">+1-555-URGENT-1</p>
                <p className="scraping-target" data-contact="emergency-email">urgent@nexusglobal.com</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Educational data for scraping practice */}
      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="contact" data-content-type="contact-info">
          <span data-main-phone="+1-555-639-8731">Main Phone</span>
          <span data-main-email="info@nexusglobal.com">Main Email</span>
          <span data-main-address="1247 Innovation Boulevard, Valdoria City">Main Address</span>
          <span data-regional-phone="+1-555-428-9900">Regional Phone</span>
          <span data-regional-email="astoria@nexusglobal.com">Regional Email</span>
          <span data-emergency-phone="+1-555-874-3681">Emergency Phone</span>
          <span data-media-email="media@nexusglobal.com">Media Email</span>
          <span data-partners-email="partners@nexusglobal.com">Partners Email</span>
        </div>
      </div>
    </>
  )
}

export default ContactPage
